import { Stack, Typography, useTheme } from "@mui/material";
import { Cog7SpikeIcon, ConfirmationBanner, InfoChip } from "components";
import { ToggleButton } from "components/toggle";
import {
  CustomerTicketStatusEditAction,
  NotificationSettingsItem,
  type StatusFilterEntry,
} from "pages/modules/control-center/notifications/components";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

export const CustomerUserNotificationSettingsItems = ({
  initialNotificationSettings,
  isCustomSettings,
  onResetClick,
}: {
  initialNotificationSettings: StatusFilterEntry[] | undefined;
  isCustomSettings: boolean;
  onResetClick: () => Promise<boolean>;
}) => {
  const [confirmationType, setConfirmationType] = useState<
    "custom" | "default" | null
  >(null);
  const { id: customerId } = useParams();
  const [isDisabled, setIsDisabled] = useState(!isCustomSettings);

  useEffect(() => {
    setIsDisabled(!isCustomSettings);
  }, [isCustomSettings]);

  const theme = useTheme();
  const TABS = [
    {
      label: "ticket",
      types: [
        { key: "new_ticket", title: "New Ticket" },
        { key: "closed_ticket", title: "Closed Ticket" },
        {
          key: "update_ticket",
          title: "Ticket Status Updated",
          hasStatusSelection: true,
        },
        { key: "chat_ticket", title: "Chat Notification" },
      ],
    },
  ];
  const tabValue = 0;

  const [searchQuery, setSearchQuery] = useState("");

  const getRoleLabel = (role_name: string) => {
    switch (role_name) {
      case "chat_members":
        return "Chat Members";
      case "manager_1":
        return "Manager";
      case "customer_user":
        return "User";
      default:
        return role_name;
    }
  };
  const userRoleTypes = [
    {
      role_id: "ce82a917-c481-4b4f-a381-c7209addc1d1",
      role_name: "manager_1",
    },
    {
      role_id: "639846fe-28cc-441c-8d5a-f2dfd3852dc2",
      role_name: "customer_user",
    },
  ].filter((role) =>
    getRoleLabel(role.role_name)
      .toLowerCase()
      .includes(searchQuery.toLowerCase()),
  );

  const handleConfirm = async () => {
    if (confirmationType === "custom") {
      const success = await onResetClick();
      success && setIsDisabled(true);
    } else {
      setIsDisabled(false);
    }
    setConfirmationType(null);
  };

  const handleCancel = () => {
    setConfirmationType(null);
  };
  return (
    <Stack
      p="8px 8px 8px 8px"
      flexGrow={1}
      sx={{
        overflowY: "auto",
      }}
    >
      <Stack
        bgcolor="neutral.main"
        borderRadius={"8px"}
        pb="78px"
        flexGrow={1}
        sx={{ overflowY: "auto" }}
      >
        <Stack sx={{ p: "8px" }}>
          <Stack
            sx={{
              p: "16px",
              gap: "16px",
              backgroundColor: theme.palette.neutral["005"],
              borderRadius: "4px",
              border: `1px solid ${theme.palette.neutral["010"]}`,
            }}
          >
            <Stack
              direction={"row"}
              sx={{
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Stack direction={"row"} sx={{ gap: "12px" }}>
                <Cog7SpikeIcon fontSize="small" />
                <Stack>
                  <Typography variant="h6">Override Global Settings</Typography>
                  <Typography
                    variant="body-small"
                    color={theme.palette.lightBg.low}
                  >
                    Enable personalized notification preferences for this
                    customer.
                  </Typography>
                </Stack>
              </Stack>
              <Stack
                direction={"row"}
                sx={{
                  gap: "12px",
                  justifyContent: "flex-end",
                  alignItems: "center",
                }}
              >
                {isCustomSettings && (
                  <Stack
                    sx={{
                      borderRadius: "4px",
                      bgcolor: "#10172E",
                      alignSelf: "center",
                      height: "24px",
                      p: "4px 8px",
                    }}
                  >
                    <Typography variant="tag-label-xs" color={"#FFF"}>
                      CUSTOM
                    </Typography>
                  </Stack>
                )}

                <ToggleButton
                  isChecked={isCustomSettings}
                  onChange={(value) => {
                    setConfirmationType(value ? "default" : "custom");
                  }}
                />
              </Stack>
            </Stack>
            {confirmationType && (
              <ConfirmationBanner
                onConfirm={handleConfirm}
                onCancel={handleCancel}
                description={
                  confirmationType === "default"
                    ? "You're about to override global settings. Custom preferences will now apply to this customer."
                    : "You're about to revert to global settings. This will remove any custom preferences for this customer."
                }
                confirmButtonLabel={"Confirm"}
                cancelButtonLabel={"Cancel"}
              />
            )}
          </Stack>
        </Stack>
        {TABS[tabValue].types.map((type, index) => (
          <NotificationSettingsItem
            key={type.key}
            initialSettings={initialNotificationSettings?.find(
              (item) => item?.notify_type === type.key,
            )}
            serviceType={`customers.${TABS[tabValue].label}`}
            notificationType={type}
            isFirstItem={index === 0}
            isLastItem={index === TABS[tabValue].types.length - 1}
            roleTypes={userRoleTypes}
            isRoleTypesDataLoading={false}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            isDisabled={isDisabled}
            {...("hasStatusSelection" in type &&
              type.hasStatusSelection && {
                titleAdornment: (
                  <InfoChip label="Customizable" tone="violet" />
                ),
                ...(customerId && {
                  endAction: (
                    <CustomerTicketStatusEditAction customerId={customerId} />
                  ),
                }),
              })}
          />
        ))}
      </Stack>
    </Stack>
  );
};
